import { useEffect, useState } from 'react';

import { trpc } from '@/lib/trpc';

import { getOfflineCache, offlineCacheKey, setOfflineCache, type OfflineCacheRecord } from './offline-store';

const BUILD_PROJECTS_CACHE_KEY = offlineCacheKey(['build.projects.list']);

type BuildProjects = NonNullable<ReturnType<typeof trpc.build.projects.list.useQuery>['data']>;

/**
 * The single build project for the caller's vessel (NavioBuild is 1:1 with a
 * vessel in M1). Returns null while loading or when none exists yet.
 */
export function useBuildProject() {
  const query = trpc.build.projects.list.useQuery(undefined, { retry: false });
  const [cachedProjects, setCachedProjects] = useState<OfflineCacheRecord<BuildProjects> | null>(null);

  useEffect(() => {
    let cancelled = false;

    getOfflineCache<BuildProjects>(BUILD_PROJECTS_CACHE_KEY).then((cached) => {
      if (!cancelled) {
        setCachedProjects(cached);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!query.data) return;

    const projectId = query.data[0]?.id ?? null;
    setOfflineCache(BUILD_PROJECTS_CACHE_KEY, query.data, {
      projectId,
      routeId: 'overview',
    }).catch(() => undefined);
  }, [query.data]);

  const cachedProject = cachedProjects?.data[0] ?? null;
  const hasLiveData = Boolean(query.data);

  return {
    project: query.data?.[0] ?? cachedProject,
    isLoading: query.isLoading && !cachedProject,
    error: hasLiveData || cachedProject ? null : query.error,
    isOfflineFallback: !hasLiveData && Boolean(cachedProject),
    cacheUpdatedAt: cachedProjects?.updatedAt ?? null,
  };
}
